import { MutationKeyConfig, QueryKeyConfig } from './api-helper';

export interface PlanListRequestArgs {
  projectID?: number;
  officeID?: number;
  customerID?: number;
  unitID?: number;
  planID?: number;
  mapLevel?: string;
  planReviewStatus?: string;
  planTempEntityID?: number;
  flagAddUnknown?: boolean;
}

const getPlanQueryStrings = (args: PlanListRequestArgs) => {
  const { customerID, officeID, projectID, unitID, planID, planReviewStatus, flagAddUnknown, mapLevel } = args;
  let queryString = '';

  if (customerID) {
    queryString += `${queryString ? '&' : ''}CustomerID=${customerID}`;
  }
  if (officeID) {
    queryString += `${queryString ? '&' : ''}OfficeID=${officeID}`;
  }
  if (projectID) {
    queryString += `${queryString ? '&' : ''}ProjectID=${projectID}`;
  }
  if (unitID) {
    queryString += `${queryString ? '&' : ''}UnitID=${unitID}`;
  }
  if (planID) {
    queryString += `${queryString ? '&' : ''}PlanID=${planID}`;
  }
  if (planReviewStatus) {
    queryString += `${queryString ? '&' : ''}PlanReviewStatusID=${planReviewStatus}`;
  }
  if (flagAddUnknown) {
    queryString += `${queryString ? '&' : ''}FlagAddUnknown=${flagAddUnknown}`;
  }
  if (mapLevel) {
    queryString += `${queryString ? '&' : ''}MapLevel=${mapLevel}`;
  }
  return queryString;
};

export const planApiKeyStore = {
  createPlan: (): MutationKeyConfig => ({
    mutationKey: [`CREATE_PLAN`],
    route: `/Plan`,
    action: 'POST',
  }),
  updatePlan: (): MutationKeyConfig => ({
    mutationKey: [`UPDATE_PLAN`],
    route: `/Plan`,
    action: 'PUT',
  }),
  deletePlan: (id: number): MutationKeyConfig => ({
    mutationKey: [`DELETE_PLAN`, id],
    route: `/Plan/DeletePlan?PlanID=${id}`,
    action: 'DELETE',
  }),
  getPlanTypes: (): QueryKeyConfig => ({
    queryKey: [`GET_PLAN_TYPES`],
    route: `/Plan/PlanType`,
  }),
  getPlanReviewTypes: (): QueryKeyConfig => ({
    queryKey: [`GET_PLAN_REVIEW_TYPES`],
    route: `/Plan/PlanReviewType`,
  }),
  getPlanFiles: (planID: number, action: boolean): QueryKeyConfig => ({
    queryKey: [`GET_PLAN_FILES`, planID, action],
    route: `/Plan/GetPlanFileList?PlanID=${planID}&ExcludePlan=${action}`,
    isEnabled: !!planID,
  }),
  deletePlanFile: (planID: number, fileID: number): MutationKeyConfig => ({
    mutationKey: [`DELETE__PLAN_FILE`, planID, fileID],
    route: `/Plan/DeletePlanFile/?PlanID=${planID}&FileID=${fileID}`,
    action: 'DELETE',
  }),
  getPlanAssignees: (): QueryKeyConfig => ({
    queryKey: [`GET_PLAN_ASSIGNEES`],
    route: `/Plan/GetPlanReviewersList`,
  }),
  getPlanReviewStatus: (): QueryKeyConfig => ({
    queryKey: [`GET_PLAN_REVIEW_STATUS`],
    route: `/Plan/PlanReviewStatus`,
  }),
  getPlanMapList: (args: PlanListRequestArgs): QueryKeyConfig => ({
    queryKey: [`GET_PLAN_MAP_LIST`, [args.customerID, args.officeID, args.projectID, args.unitID, args.mapLevel].filter((a) => a)],
    route: `/Plan/GetMapList?${getPlanQueryStrings(args)}`,
  }),
  mapPlans: (): MutationKeyConfig => ({
    mutationKey: [`MAP_PLANS`],
    route: `/Plan/MapPlans`,
    action: 'POST',
  }),
  getPlans: (args: PlanListRequestArgs): QueryKeyConfig => ({
    queryKey: [
      `GET_PLANS`,
      ...[
        args.planTempEntityID,
        args.officeID,
        args.projectID,
        args.unitID,
        args.planReviewStatus,
        args.planTempEntityID,
        args.flagAddUnknown,
      ].filter((a) => a),
    ],
    route: `/Plan?${getPlanQueryStrings(args)}`,
  }),
  getPlanDashboardCount: (args: PlanListRequestArgs): QueryKeyConfig => ({
    queryKey: [`GET_PLAN_DARHBOARD_COUNT`, ...[args.customerID, args.officeID, args.projectID].filter((a) => a)],
    route: `/Plan/PlanDashboardCount?${getPlanQueryStrings(args)}`,
  }),
  getPlanDashboardList: (args: PlanListRequestArgs): QueryKeyConfig => ({
    queryKey: [`GET_PLAN_DARHBOARD_LIST`, ...[args.customerID, args.officeID, args.projectID].filter((a) => a)],
    route: `/Plan/PlanDashboardList?${getPlanQueryStrings(args)}`,
  }),
  updatePlanStatus: (): MutationKeyConfig => ({
    mutationKey: [`UPDATE_PLAN_STATUS`],
    route: `/Plan/ChangePlanStatus`,
    action: 'PUT',
  }),
  downloadLinkByID: (id?: number): QueryKeyConfig => ({
    queryKey: [`GET_DOWNLOAD_LINK_BY_ID`, id],
    route: `/Plan/${id}/DownloadPlanFile`,
  }),
  getPlanFileTypes: (): QueryKeyConfig => ({
    queryKey: [`GET_PLAN_FILE_TYPES`],
    route: `/Plan/PlanFileType`,
  }),
  getPlansByCustomerAndOffice: (customerID: number, officeID: any): QueryKeyConfig => ({
    queryKey: [`GET_PLANS_BY_CUSTOMER_AND_OFFICE`, customerID, officeID],
    route: `/Plan?CustomerID=${customerID}&OfficeID=${officeID}`,
    isEnabled: !!customerID && !!officeID
  }),
  getPlansByCustomerAndOfficeAndForAssociation: (customerID: number, officeID: any, forAssociation: any): QueryKeyConfig => ({
    queryKey: [`GET_PLANS_BY_CUSTOMER_AND_OFFICE_AND_ASSOCIATION`, customerID, officeID, forAssociation],
    route: `/Plan?CustomerID=${customerID}&OfficeID=${officeID}&ForAssociation=${forAssociation}`,
    isEnabled: !!customerID && !!officeID
  }),
  getPlansByUnit: (unitID: number): QueryKeyConfig => ({
    queryKey: [`GET_PLANS_BY_UNIT`, unitID],
    route: `/Plan?UnitID=${unitID}`,
    isEnabled: !!unitID
  }),
  getPlansByUnitAndForAssociation: (unitID: number, forAssociation: any): QueryKeyConfig => ({
    queryKey: [`GET_PLANS_BY_UNIT_AND_FOR_ASSOCIATION`, unitID, forAssociation],
    route: `/Plan?UnitID=${unitID}&ForAssociation=${forAssociation}`,
    isEnabled: !!unitID
  }),
  getMappingPlans: (planID: any): QueryKeyConfig => ({
    queryKey: [`GET_MAPPING_PLANS`, planID],
    route: `/Plan/GetPlanLibraryMapList/${planID}`,
    isEnabled: !!planID && planID > 0,
  }),
  deleteMapPlan: (args: PlanListRequestArgs): MutationKeyConfig => ({
    mutationKey: [`DELETE_MAP_PLAN`, ...[args.officeID, args.projectID, args.unitID, args.planID].filter((a) => a)],
    route: `/Plan/RemovePlanMapRequest?${getPlanQueryStrings(args)}`,
    action: 'DELETE',
  }),
  removeMapPlan: (): MutationKeyConfig => ({
    mutationKey: [`REMOVE_MAP_PLAN`],
    route: `/Plan/RemovePlanMappings`,
    action: 'POST',
  }),
};
